import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import './InternetApply.css';

interface FormData {
  name: string;
  phone: string;
  birthDate: string;
  address: string;
  addressDetail: string;
  isUsingInternet: boolean;
  currentCarrier: string;
  currentExpiryDate: string;
  applyTv: boolean;
  applyRouter: boolean;
  installDate: string;
  requests: string;
}

const InternetApply: React.FC = () => {
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get('room_id');
  const { customer } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    birthDate: '',
    address: '',
    addressDetail: '',
    isUsingInternet: false,
    currentCarrier: '',
    currentExpiryDate: '',
    applyTv: false,
    applyRouter: false,
    installDate: '',
    requests: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        name: customer.name || '',
        phone: customer.phone || '',
      }));
    }
  }, [customer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.address) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('internet_applications')
        .insert([{
          customer_id: customer?.id,
          room_id: roomId,
          name: formData.name,
          phone: formData.phone,
          birth_date: formData.birthDate || null,
          address: formData.address,
          address_detail: formData.addressDetail || null,
          is_using_internet: formData.isUsingInternet,
          current_carrier: formData.currentCarrier || null,
          current_expiry_date: formData.currentExpiryDate || null,
          apply_tv: formData.applyTv,
          apply_router: formData.applyRouter,
          install_date: formData.installDate || null,
          requests: formData.requests || null,
          status: 'pending',
        }]);

      if (error) throw error;

      if (roomId) {
        await supabase.from('messages').insert([{
          room_id: roomId,
          sender_id: '00000000-0000-0000-0000-000000000000',
          message_type: 'text',
          content: `[인터넷/TV 가입신청서가 접수되었습니다]\n\n신청자: ${formData.name}\n연락처: ${formData.phone}\n설치주소: ${formData.address} ${formData.addressDetail || ''}\n희망설치일: ${formData.installDate || '미정'}\nTV신청: ${formData.applyTv ? '예' : '아니오'}\n공유기신청: ${formData.applyRouter ? '예' : '아니오'}`,
          is_read: false
        }]);
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('신청서 제출 오류:', error);
      alert('신청서 제출에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="internet-apply-container">
        <div className="apply-success">
          <div className="success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="32" fill="#eee0af"/>
              <path d="M20 32L28 40L44 24" stroke="#1e1e1e" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2>신청이 완료되었습니다</h2>
          <p>담당 상담원이 빠른 시일 내에<br/>연락드리겠습니다.</p>
          <button className="close-btn" onClick={() => window.close()}>
            창 닫기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="internet-apply-container">
      <div className="apply-header">
        <h1>인터넷/TV 가입신청서</h1>
      </div>

      <form className="apply-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">이름 <span className="required">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="이름을 입력해주세요."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">연락처 <span className="required">*</span></label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="연락처를 입력해주세요."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="birthDate">생년월일</label>
          <input
            type="date"
            id="birthDate"
            name="birthDate"
            value={formData.birthDate}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="address">설치주소 <span className="required">*</span></label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="설치주소를 입력해주세요."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="addressDetail">상세주소</label>
          <input
            type="text"
            id="addressDetail"
            name="addressDetail"
            value={formData.addressDetail}
            onChange={handleChange}
            placeholder="상세주소를 입력해주세요."
          />
        </div>

        <div className="form-divider" />

        <div className="form-group toggle-group">
          <label>현 인터넷 사용여부</label>
          <div className="toggle-buttons">
            <button
              type="button"
              className={`toggle-btn ${formData.isUsingInternet ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, isUsingInternet: true }))}
            >
              사용중
            </button>
            <button
              type="button"
              className={`toggle-btn ${!formData.isUsingInternet ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, isUsingInternet: false }))}
            >
              미사용
            </button>
          </div>
        </div>

        {formData.isUsingInternet && (
          <>
            <div className="form-group">
              <label htmlFor="currentCarrier">현 인터넷 통신사</label>
              <select
                id="currentCarrier"
                name="currentCarrier"
                value={formData.currentCarrier}
                onChange={handleChange}
              >
                <option value="">선택해주세요.</option>
                <option value="KT">KT</option>
                <option value="SK브로드밴드">SK브로드밴드</option>
                <option value="LG U+">LG U+</option>
                <option value="기타">기타</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="currentExpiryDate">현 인터넷 만기일</label>
              <input
                type="date"
                id="currentExpiryDate"
                name="currentExpiryDate"
                value={formData.currentExpiryDate}
                onChange={handleChange}
              />
            </div>
          </>
        )}

        <div className="form-divider" />

        <div className="form-group toggle-group">
          <label>TV신청 여부</label>
          <div className="toggle-buttons">
            <button
              type="button"
              className={`toggle-btn ${formData.applyTv ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, applyTv: true }))}
            >
              신청
            </button>
            <button
              type="button"
              className={`toggle-btn ${!formData.applyTv ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, applyTv: false }))}
            >
              미신청
            </button>
          </div>
        </div>

        <div className="form-group toggle-group">
          <label>공유기 신청여부</label>
          <div className="toggle-buttons">
            <button
              type="button"
              className={`toggle-btn ${formData.applyRouter ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, applyRouter: true }))}
            >
              신청
            </button>
            <button
              type="button"
              className={`toggle-btn ${!formData.applyRouter ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, applyRouter: false }))}
            >
              미신청
            </button>
          </div>
        </div>

        <div className="form-divider" />

        <div className="form-group">
          <label htmlFor="installDate">설치 희망일</label>
          <input
            type="date"
            id="installDate"
            name="installDate"
            value={formData.installDate}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="requests">요청사항</label>
          <textarea
            id="requests"
            name="requests"
            value={formData.requests}
            onChange={handleChange}
            placeholder="요청사항을 입력해주세요."
            rows={4}
          />
        </div>

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? '제출 중...' : '신청하기'}
        </button>
      </form>
    </div>
  );
};

export default InternetApply;
